import React, {useState, useMemo} from "react";
import {Booking} from "../../types";
import {
    getBookingById,
    updateBookingStatus,
    checkBooking,
} from "../../api/api";
import {Button} from "../ui/button";
import {Badge} from "../ui/badge";
import {formatCurrency} from "../../lib/utils";
import {
    Calendar,
    Clock,
    Users,
    Check,
    X as XIcon,
    Eye,
} from "lucide-react";
import VerifyCustomerModal from "../booking/VerifyCustomerModal";
import CheckBookingVerificationModal from "../booking/CheckBookingVerificationModal";

interface BookingsTableProps {
    bookings: Booking[];
    refreshBookings: () => void;
}

const getStatusColor = (status: string) => {
    switch (status) {
        case "confirmed":
            return "bg-green-500 hover:bg-green-600";
        case "completed":
            return "bg-blue-500 hover:bg-blue-600";
        case "cancelled":
            return "bg-red-500 hover:bg-red-600";
        default:
            return "bg-gray-500 hover:bg-gray-600";
    }
};

const getStatusText = (status: string) => {
    switch (status) {
        case "confirmed":
            return "Confirmed";
        case "completed":
            return "Completed";
        case "cancelled":
            return "Cancelled";
        case "awaiting_restaurant_response":
            return "Awaiting Response";
        default:
            return status;
    }
};

export default function BookingsTable({bookings, refreshBookings}: BookingsTableProps) {
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

    // Modal states for verification
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationBooking, setVerificationBooking] = useState<Booking | null>(null);
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    // Group bookings by date (YYYY-MM-DD)
    const bookingsByDate = useMemo(() => {
        const map: Record<string, Booking[]> = {};
        bookings.forEach(b => {
            if (!map[b.date]) map[b.date] = [];
            map[b.date].push(b);
        });
        Object.keys(map).forEach(date =>
            map[date].sort((a, b) => a.time.localeCompare(b.time))
        );
        return Object.fromEntries(
            Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]))
        );
    }, [bookings]);

    // Actions
    const handleConfirm = async (id: number) => {
        try {
            await updateBookingStatus(id, "confirmed");
            await refreshBookings();
        } catch (e) {
            console.error(e);
        }
    };
    const handleCancel = async (id: number) => {
        try {
            await updateBookingStatus(id, "cancelled");
            await refreshBookings();
        } catch (e) {
            console.error(e);
        }
    };

    const openDetails = async (id: number) => {
        try {
            const data = await getBookingById(id);
            setSelectedBooking(data);
        } catch (e) {
            console.error(e);
        }
    };
    const closeDetails = () => setSelectedBooking(null);

    // --- Verification logic, identical to header ---

    const handleVerifyCustomer = async (code: string) => {
        setIsVerifying(true);
        try {
            const booking = await checkBooking(code);
            if (booking) {
                setVerificationBooking(booking);
                setShowVerificationModal(true);
            }
            setIsVerifyModalOpen(false);
        } catch (error) {
            // Optionally show error (optional)
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <div className="space-y-8">
            {Object.keys(bookingsByDate).length === 0 && (
                <div
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center text-gray-600 dark:text-gray-300">
                    No bookings to display
                </div>
            )}

            {Object.entries(bookingsByDate).map(([date, bookings]) => (
                <div key={date}
                     className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow">
                    <div className="flex items-center mb-4">
                        <Calendar className="h-5 w-5 text-primary mr-2"/>
                        <span className="text-lg font-bold text-gray-800 dark:text-white">{date}</span>
                        <span className="ml-4 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {bookings.length} Booking{bookings.length > 1 ? "s" : ""}
            </span>
                    </div>
                    <div className="space-y-4">
                        {bookings.map(booking => {
                            const isCompleted = booking.status === "completed";
                            const isCancelled = booking.status === "cancelled";
                            return (
                                <div
                                    key={booking.id}
                                    className={
                                        `flex flex-col md:flex-row items-center justify-between gap-4 border-b border-dashed border-gray-200 dark:border-gray-700 pb-4 last:border-0
                    transition ${isCompleted ? "opacity-50 select-none" : ""} ${isCancelled ? "opacity-60" : ""}`
                                    }
                                    style={isCompleted ? {filter: "grayscale(0.5)"} : {}}
                                >
                                    {/* Info Section */}
                                    <div className="flex-1 flex flex-col md:flex-row items-start md:items-center gap-2">
                                        <div className="flex items-center gap-2 min-w-[75px]">
                                            <Clock className="h-4 w-4 text-gray-500"/>
                                            <span className="font-semibold text-lg">{booking.time}</span>
                                        </div>
                                        <div className="flex items-center gap-2 min-w-[90px]">
                                            <Users className="h-4 w-4 text-gray-500"/>
                                            <span>{booking.guests} Guest{booking.guests > 1 ? "s" : ""}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-500">Total:</span>
                                            <span className="font-semibold">{formatCurrency(booking.total_price)}</span>
                                        </div>
                                        <div>
                                            <Badge className={getStatusColor(booking.status)}>
                                                {getStatusText(booking.status)}
                                            </Badge>
                                        </div>
                                    </div>
                                    {/* Action Section */}
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openDetails(booking.id)}
                                        >
                                            <Eye className="mr-2 h-4 w-4"/> Details
                                        </Button>
                                        {!isCompleted && !isCancelled && (
                                            <>
                                                {booking.status === "awaiting_restaurant_response" && (
                                                    <>
                                                        <Button
                                                            variant="default"
                                                            size="sm"
                                                            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                                                            onClick={() => handleConfirm(booking.id)}
                                                        >
                                                            <Check className="mr-2 h-4 w-4" />
                                                            Confirm Booking
                                                        </Button>
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => handleCancel(booking.id)}
                                                        >
                                                            <XIcon className="mr-2 h-4 w-4"/> Cancel
                                                        </Button>
                                                    </>
                                                )}
                                                {booking.status === "confirmed" && (
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                                                        onClick={() => setIsVerifyModalOpen(true)}
                                                    >
                                                        <span className="inline-block w-2 h-2 rounded-full bg-white/80 mr-1 animate-pulse" />

                                                        Verify Customer
                                                    </Button>

                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Booking Details Modal */}
            {selectedBooking && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-25">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-sm w-full p-6 space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                Booking #{selectedBooking.id}
                            </h2>
                            <Button variant="ghost" size="icon" onClick={closeDetails}>
                                <XIcon className="h-5 w-5 text-gray-500 dark:text-gray-300"/>
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-800 dark:text-gray-200">
                            <span className="font-medium">Date</span>
                            <span>{selectedBooking.date}</span>
                            <span className="font-medium">Time</span>
                            <span>{selectedBooking.time}</span>
                            <span className="font-medium">Guests</span>
                            <span>{selectedBooking.guests}</span>
                            <span className="font-medium">Total Price</span>
                            <span>{formatCurrency(selectedBooking.total_price)}</span>
                            <span className="font-medium">Status</span>
                            <span>
                <Badge className={getStatusColor(selectedBooking.status)}>
                  {getStatusText(selectedBooking.status)}
                </Badge>
              </span>
                            <span className="font-medium">Dietary Notes</span>
                            <span>{selectedBooking.dietary || "–"}</span>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={closeDetails}>Close</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Verify Customer Modal (code input) */}
            {isVerifyModalOpen && (
                <VerifyCustomerModal
                    isOpen={isVerifyModalOpen}
                    onClose={() => setIsVerifyModalOpen(false)}
                    onVerify={handleVerifyCustomer}
                    isVerifying={isVerifying}
                />
            )}

            {/* Show booking details after verification */}
            {showVerificationModal && (
                <CheckBookingVerificationModal
                    open={showVerificationModal}
                    booking={verificationBooking}
                    onClose={() => {
                        setShowVerificationModal(false);
                        setVerificationBooking(null);
                    }}
                />
            )}
        </div>
    );
}
